import { parcoCstai, parcoEcobel, parcoEmaComfort, type ImpiantoDelParco } from '@emacomfort/device-client';

/**
 * Marchi (white-label). Il pannello porta il marchio dell'installatore che lo
 * rivende, non quello della piattaforma: il nome Ema Comfort compare solo in
 * Impostazioni › Informazioni.
 */
export type MarchioId = 'emacomfort' | 'cstai' | 'ecobel';

export interface Marchio {
  id: MarchioId;
  nome: string;
  /** Riga sotto il nome, nel pannello. */
  claim: { it: string; en: string };
  /** Colore d'accento del marchio; deve reggere testo bianco in grassetto ≥ 20 px (AA testo grande). */
  accento: string;
  accentoForte: string;
  accentoContrasto: string;
  monogramma: string;
  tenantId: string;
  /** `hardware` = vende la propria centralina; `integratore` = unifica sistemi di terzi. */
  profilo: 'hardware' | 'integratore';
  parco: () => ImpiantoDelParco[];
}

export const MARCHI: Record<MarchioId, Marchio> = {
  emacomfort: {
    id: 'emacomfort',
    nome: 'Ema Comfort',
    claim: { it: 'Gestione impianti', en: 'Installation management' },
    accento: '#7c5cff',
    accentoForte: '#9b82ff',
    accentoContrasto: '#ffffff',
    monogramma: 'EC',
    tenantId: 'emacomfort',
    profilo: 'hardware',
    parco: parcoEmaComfort,
  },
  cstai: {
    id: 'cstai',
    nome: 'CSTAI',
    claim: { it: 'Centralina domotica · gestione centrale termica', en: 'Home controller · plant room management' },
    accento: '#2f7fc1',
    accentoForte: '#5ea2dd',
    accentoContrasto: '#ffffff',
    monogramma: 'C',
    tenantId: 'cstai',
    profilo: 'hardware',
    parco: parcoCstai,
  },
  ecobel: {
    id: 'ecobel',
    nome: 'Ecobel',
    claim: { it: 'Fotovoltaico e pompe di calore · un impianto, una schermata', en: 'Solar and heat pumps · one installation, one screen' },
    accento: '#2e8f57',
    accentoForte: '#4fb877',
    accentoContrasto: '#ffffff',
    monogramma: 'E',
    tenantId: 'ecobel',
    profilo: 'integratore',
    parco: parcoEcobel,
  },
};

export const MARCHIO_PREDEFINITO: MarchioId = 'cstai';

/** Il marchio senza installatore: la piattaforma per quello che e'. E' cio'
 *  che indossa un indirizzo pubblico, dove presentarsi con il nome di un
 *  cliente vero sarebbe un'affermazione su di lui, non su di noi. */
export const MARCHIO_NEUTRO: MarchioId = 'emacomfort';

export function isMarchioId(v: string | null | undefined): v is MarchioId {
  return v === 'emacomfort' || v === 'cstai' || v === 'ecobel';
}

function hexToRgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function applicaMarchio(m: Marchio): void {
  const root = document.documentElement;
  root.style.setProperty('--accent', m.accento);
  root.style.setProperty('--accent-strong', m.accentoForte);
  root.style.setProperty('--accent-contrast', m.accentoContrasto);
  root.style.setProperty('--accent-bg', hexToRgba(m.accento, 0.18));
  root.dataset.marchio = m.id;
}
